/**
 * =============================================================================
 * TENANT MEDIA SETTINGS RESOLVER (ADR-022 §10.1 — PR4b)
 * =============================================================================
 *
 * The SINGLE fail-closed resolver for the tenant-configurable image-attachment
 * settings, mirroring the `resolvePublicLaunchMode` pattern (`launchMode.ts`):
 * a pure function over the persisted `stores.settings` JSON keys that clamps
 * every value to documented safe bounds and never trusts a persisted value.
 * Shared by the worker (enforcement on every upload path), the admin UI (the
 * settings card displays EFFECTIVE — clamped — values), and, later, the
 * tenant-aware widget config (PR5, BL-IMG-WIDGET-STAGING-COMPRESSION-001).
 *
 * DESIGN NOTES (§10.1, verbatim requirements)
 * -------------------------------------------
 *   - ABSENT vs CORRUPT asymmetry for the master toggle: a tenant that never
 *     configured `imageAttachmentsEnabled` gets the default `true`; a CORRUPT
 *     or UNREADABLE settings read is treated as DISABLED (fail-closed). The
 *     pure resolver handles per-KEY corruption (non-boolean → false); the
 *     worker's DB wrapper (`getStoreMediaSettings`) owns the unreadable-READ
 *     case and returns {@link DISABLED_MEDIA_SETTINGS}.
 *   - `publicLaunchMode = HIDDEN` remains the OUTER gate regardless — these
 *     settings never open a surface the launch gate keeps closed.
 *   - Clamps only ever TIGHTEN toward the platform constants: no tenant value
 *     can raise a cap above `FILE_VALIDATION` / exceed the hard size ceiling.
 *   - Key naming: the per-question cap is persisted as `maxImagesPerSubmission`
 *     (live since PR0/G5 — seeded at install, enforced on the question upload
 *     path). §10.1 names it `maxImagesPerQuestion`; the resolver exposes the
 *     ADR name on the RESOLVED shape while reading the live persisted key, so
 *     no DTO/seed rename ripples through the widget config or admin client.
 *   - `staffImageRequireReview` is deliberately NOT resolved or persisted —
 *     deferred (D-SM); staff auto-approve is the baked default (ADR-022 §5).
 *   - Retroactivity (D-SETTINGS) is upload-time-only in PR4b (owner-approved
 *     fork, 2026-07-04): changed settings govern NEW uploads; existing
 *     approved images are untouched until the display PRs land read-time
 *     semantics. The §6.1 public gate is deliberately not consulted here.
 * =============================================================================
 */

import { FILE_VALIDATION, type AllowedMimeType } from './constants';

/**
 * Customer/guest moderation posture (`imageModeration`, ADR-022 §10.1):
 *   REQUIRE_APPROVAL — customer uploads land PENDING_REVIEW (default-hidden).
 *   AUTO_APPROVE     — customer uploads land APPROVED (tenant accepts the risk).
 *   DISABLED         — customer uploads are rejected outright.
 * Governs CUSTOMER/GUEST paths ONLY — staff auto-approve is author-trust
 * (ADR-022 §5), not posture, and staff uploads ignore this key entirely.
 */
export const IMAGE_MODERATION_POSTURES = [
  'REQUIRE_APPROVAL',
  'AUTO_APPROVE',
  'DISABLED',
] as const;
export type ImageModerationPosture = (typeof IMAGE_MODERATION_POSTURES)[number];

/** Type guard for a persisted/untrusted posture value. */
export function isImageModerationPosture(value: unknown): value is ImageModerationPosture {
  return (IMAGE_MODERATION_POSTURES as readonly string[]).includes(value as string);
}

/**
 * The resolved, clamped, enforcement-ready media settings for one tenant.
 * Every consumer reads THIS shape — never the raw persisted keys.
 */
export interface EffectiveMediaSettings {
  /**
   * Master toggle. `false` blocks EVERY upload path — customer question,
   * customer reply, AND staff authoring (owner-approved fork, 2026-07-04: the
   * master toggle means off for everyone; `imageModeration = DISABLED` is the
   * customer-only knob). Management of EXISTING media (show/hide/remove and
   * the moderation queue) stays available — management is not authoring.
   */
  imageAttachmentsEnabled: boolean;
  /**
   * Per-question cap (§10.1 `maxImagesPerQuestion`; persisted as the live key
   * `maxImagesPerSubmission`). Clamp 1..MAX_IMAGES_PER_QUESTION. Applies to
   * question-anchored uploads by EVERY author — §10.1 defines no staff
   * carve-out on the question cap.
   */
  maxImagesPerQuestion: number;
  /**
   * Customer per-reply cap (D-RC — tenant-customizable, default 5). Clamp
   * 0..MAX_IMAGES_PER_REPLY; 0 disables customer reply images entirely.
   */
  maxImagesPerReply: number;
  /**
   * Staff per-reply allowance (D-RC / round-4 Composer F-09 — an explicit
   * setting, not prose). Clamp 0..MAX_IMAGES_PER_STAFF_REPLY; 0 disables
   * staff reply-image authoring. Enforced as the author-type-resolved bound
   * of the atomic INSERT…SELECT cap (the admin route passes it; the customer
   * routes pass `maxImagesPerReply` — the per-call bound IS the
   * author-type-aware branch, since author type is fixed per endpoint and
   * staff-authored replies are never guest-uploadable, nor vice versa).
   * Always within the per-tenant aggregate ceiling (D-QUOTA).
   */
  maxImagesPerStaffReply: number;
  /** Per-file size cap in MB. Clamp 1..MAX_SIZE_MB (the hard ceiling). */
  maxImageSizeMb: number;
  /**
   * Allowed upload formats — a validated SUBSET of the platform allow-list.
   * Unknown members are dropped; an empty/invalid result falls back to the
   * FULL platform set (a typo must never silently brick uploads — the master
   * toggle is the disable switch). Server validation remains the gate.
   */
  allowedImageFormats: readonly AllowedMimeType[];
  /** Customer/guest moderation posture. Missing/invalid → REQUIRE_APPROVAL. */
  imageModeration: ImageModerationPosture;
}

/** §10.1 defaults (also the full fallback for a never-configured tenant). */
export const MEDIA_SETTINGS_DEFAULTS: EffectiveMediaSettings = {
  imageAttachmentsEnabled: true,
  maxImagesPerQuestion: FILE_VALIDATION.MAX_IMAGES_PER_QUESTION,
  maxImagesPerReply: FILE_VALIDATION.MAX_IMAGES_PER_REPLY,
  maxImagesPerStaffReply: FILE_VALIDATION.MAX_IMAGES_PER_STAFF_REPLY,
  maxImageSizeMb: FILE_VALIDATION.MAX_SIZE_MB,
  allowedImageFormats: FILE_VALIDATION.ALLOWED_MIME_TYPES,
  imageModeration: 'REQUIRE_APPROVAL',
} as const;

/**
 * The fail-closed posture for an UNREADABLE settings read (§10.1: "a
 * corrupt/unreadable resolver read → treat as disabled"). Only the master
 * toggle differs from the defaults — with it `false`, no upload path consults
 * the other values. Returned by the worker's `getStoreMediaSettings` wrapper
 * when the underlying read/parse throws, AND by the pure resolver itself when
 * the persisted blob is structurally corrupt (a non-object — see
 * {@link resolveMediaSettings}); a readable per-key-corrupt OBJECT still
 * resolves per key.
 */
export const DISABLED_MEDIA_SETTINGS: EffectiveMediaSettings = {
  ...MEDIA_SETTINGS_DEFAULTS,
  imageAttachmentsEnabled: false,
} as const;

/**
 * The raw persisted keys the resolver reads (all optional — the
 * `StoreSettings` JSON blob). Typed `unknown` per key: persisted values are
 * untrusted (hand-set rows, legacy writes) and every one is validated here.
 */
export interface RawMediaSettingsInput {
  imageAttachmentsEnabled?: unknown;
  /** Live persisted key for the §10.1 `maxImagesPerQuestion` cap (see header). */
  maxImagesPerSubmission?: unknown;
  maxImagesPerReply?: unknown;
  maxImagesPerStaffReply?: unknown;
  maxImageSizeMb?: unknown;
  allowedImageFormats?: unknown;
  imageModeration?: unknown;
}

/**
 * Canonicalize a tenant format subset: keep only known platform members,
 * deduped IN PLATFORM ORDER (deterministic persisted/resolved form). Returns
 * null when the input is not a usable subset (non-array / empty / no valid
 * members) — the caller picks the fallback (the resolver falls back to the
 * FULL platform set; the settings PUT rejects instead). ONE algorithm for the
 * write path and the read path, so persistence and resolution can never
 * disagree on ordering/dedup (PR4b backbone-review fold R5).
 */
export function normalizeAllowedFormats(raw: unknown): AllowedMimeType[] | null {
  if (!Array.isArray(raw)) return null;
  const requested = new Set(raw.filter((v): v is string => typeof v === 'string'));
  const subset = FILE_VALIDATION.ALLOWED_MIME_TYPES.filter((m) => requested.has(m));
  return subset.length > 0 ? [...subset] : null;
}

/**
 * Clamp an untrusted numeric setting into [min, max]. Non-finite / non-number
 * values (and fractional values, truncated) fall back to `fallback` — a
 * malformed persisted value must resolve to the documented default, never to
 * an accidental 0 or an uncapped bound.
 */
function clampIntSetting(
  value: unknown,
  min: number,
  max: number,
  fallback: number
): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.min(Math.max(Math.trunc(value), min), max);
}

/**
 * Fail-closed §10.1 resolver — pure, no I/O. Reads the raw persisted keys and
 * returns clamped, enforcement-ready values (see {@link EffectiveMediaSettings}
 * for per-key semantics). Callers that read from the DATABASE must go through
 * the worker's `getStoreMediaSettings` wrapper, which maps an unreadable read
 * to {@link DISABLED_MEDIA_SETTINGS}; this function owns per-key resolution
 * PLUS the corrupt-SHAPE case below.
 */
export function resolveMediaSettings(
  raw: RawMediaSettingsInput | null | undefined
): EffectiveMediaSettings {
  // §10.1 fail-closed at the CONTAINER level. The persisted `settings` blob is
  // an `as`-cast JSON column (compile-time typing only), so at runtime `raw`
  // can be a structurally-corrupt value — an array or a scalar from a bad
  // migration or a hand-edited row. ABSENT (null/undefined) means "never
  // configured" → the enabled defaults below; a PRESENT non-plain-object is a
  // CORRUPT value → DISABLED (never silently enabled at max caps), mirroring the
  // per-key "present non-boolean toggle → false" rule one level up. Together with
  // the wrapper's unreadable-READ → DISABLED, this closes the fail-closed contract
  // for every non-per-key-object shape. (PR4b internal adversarial review.)
  const rawValue = raw as unknown;
  if (
    rawValue !== null &&
    rawValue !== undefined &&
    (typeof rawValue !== 'object' || Array.isArray(rawValue))
  ) {
    return DISABLED_MEDIA_SETTINGS;
  }

  const input = raw ?? {};

  // Master toggle — the §10.1 asymmetry. ABSENT means the key was never
  // persisted (`undefined`) → default true. EVERYTHING else that is not the
  // literal boolean `true` is a corrupt/unexpected value → false (fail-closed):
  // this INCLUDES a persisted JSON `null` (`null !== undefined`, so it takes the
  // false branch, NOT the absent→true branch). The PUT handler's
  // `typeof !== 'boolean'` guard means the API never writes `null`, so only a
  // hand-edited row or a bad migration reaches the null case — and it fails
  // closed (external review REV-Copilot-01: comment precision, no behavior
  // change).
  const toggleRaw = input.imageAttachmentsEnabled;
  const imageAttachmentsEnabled =
    toggleRaw === undefined ? true : toggleRaw === true;

  // Formats: the shared canonicalization (normalizeAllowedFormats); an
  // unusable subset → full platform set (see
  // EffectiveMediaSettings.allowedImageFormats).
  const allowedImageFormats: readonly AllowedMimeType[] =
    normalizeAllowedFormats(input.allowedImageFormats) ??
    FILE_VALIDATION.ALLOWED_MIME_TYPES;

  return {
    imageAttachmentsEnabled,
    maxImagesPerQuestion: clampIntSetting(
      input.maxImagesPerSubmission,
      1,
      FILE_VALIDATION.MAX_IMAGES_PER_QUESTION,
      MEDIA_SETTINGS_DEFAULTS.maxImagesPerQuestion
    ),
    maxImagesPerReply: clampIntSetting(
      input.maxImagesPerReply,
      0, // 0 disables customer reply images (D-RC)
      FILE_VALIDATION.MAX_IMAGES_PER_REPLY,
      MEDIA_SETTINGS_DEFAULTS.maxImagesPerReply
    ),
    maxImagesPerStaffReply: clampIntSetting(
      input.maxImagesPerStaffReply,
      0, // 0 disables staff reply-image authoring (symmetry with maxImagesPerReply)
      FILE_VALIDATION.MAX_IMAGES_PER_STAFF_REPLY,
      MEDIA_SETTINGS_DEFAULTS.maxImagesPerStaffReply
    ),
    maxImageSizeMb: clampIntSetting(
      input.maxImageSizeMb,
      1,
      FILE_VALIDATION.MAX_SIZE_MB,
      MEDIA_SETTINGS_DEFAULTS.maxImageSizeMb
    ),
    allowedImageFormats,
    imageModeration: isImageModerationPosture(input.imageModeration)
      ? input.imageModeration
      : MEDIA_SETTINGS_DEFAULTS.imageModeration,
  };
}
